import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { createTheme, Theme } from '@mui/material/styles';

// Define Theme types
type ThemeMode = 'light' | 'dark';

// Define context type
interface ThemeContextType {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  theme: Theme;
}

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  themeMode: 'light',
  toggleThemeMode: () => {},
  setThemeMode: () => {},
  theme: createTheme(),
});

// Create light and dark themes
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4776E6',
    },
    secondary: {
      main: '#8E54E9',
    },
    error: {
      main: '#FF4136',
    },
    warning: {
      main: '#FF851B',
    },
    success: {
      main: '#2ECC40',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Segoe UI', sans-serif",
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 20px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: 12,
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4776E6',
    },
    secondary: {
      main: '#8E54E9',
    },
    error: {
      main: '#FF4136',
    },
    warning: {
      main: '#FF851B',
    },
    success: {
      main: '#2ECC40',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Segoe UI', sans-serif",
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 20px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          borderRadius: 12,
        },
      },
    },
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

// Create the provider
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Check if there's a stored preference in localStorage
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const storedTheme = localStorage.getItem('themeMode');
    return (storedTheme as ThemeMode) || 'light';
  });

  const theme = themeMode === 'light' ? lightTheme : darkTheme;

  // Toggle between light and dark
  const toggleThemeMode = () => {
    setThemeMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };
  
  // Direct mode setter
  const setThemeModeDirectly = (mode: ThemeMode) => {
    localStorage.setItem('themeMode', mode);
    setThemeMode(mode);
  };

  // Effect to apply theme preference to body element
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${themeMode}-mode`);
  }, [themeMode]);

  return (
    <ThemeContext.Provider value={{ 
      themeMode, 
      toggleThemeMode, 
      setThemeMode: setThemeModeDirectly, 
      theme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

export default ThemeContext; 